import { JSX } from "react";
import { Page, View, Document } from "@react-pdf/renderer";
import { styles, spacing } from "components/Resume/ResumePDF/styles";
import { ResumePDFProfile } from "components/Resume/ResumePDF/ResumePDFProfile";
import { ResumePDFWorkExperience } from "components/Resume/ResumePDF/ResumePDFWorkExperience";
import { ResumePDFEducation } from "components/Resume/ResumePDF/ResumePDFEducation";
import { ResumePDFProject } from "components/Resume/ResumePDF/ResumePDFProject";
import { ResumePDFSkills } from "components/Resume/ResumePDF/ResumePDFSkills";
import { ResumePDFCustom } from "components/Resume/ResumePDF/ResumePDFCustom";
import { DEFAULT_FONT_COLOR } from "lib/redux/settingsSlice";
import type { Settings, ShowForm } from "lib/redux/settingsSlice";
import type { Resume } from "lib/redux/types";
import { SuppressResumePDFErrorMessage } from "components/Resume/ResumePDF/common/SuppressResumePDFErrorMessage";
import { PDFViewer } from "@react-pdf/renderer";
import { ResumePDFCertificate } from "./ResumePDFCertificate";
import { ResumePDFLanguage } from "./ResumePDFLanguage";
import { ResumePDFAward } from "./ResumePDFAward";
import { FontSizeProvider } from "./FontSizeContext";

/**
 * Note: ResumePDF is supposed to be rendered inside PDFViewer. However,
 * PDFViewer is rendered too slow and has noticeable delay as you enter
 * the resume form, so we render it without PDFViewer to make it render
 * instantly. There are 2 drawbacks with this approach:
 * 1. Not everything works out of box if not rendered inside PDFViewer,
 *    e.g. svg doesn't work, so it takes in a isPDF flag that maps react
 *    pdf element to the correct dom element.
 * 2. It throws a lot of errors in console log, e.g. "<VIEW /> is using incorrect
 *    casing. Use PascalCase for React components, or lowercase for HTML elements."
 *    in development, causing a lot of noises. We can possibly workaround this by
 *    mapping every react pdf element to a dom element, but for now, we simply
 *    suppress these messages in <SuppressResumePDFErrorMessage />.
 *    https://github.com/diegomura/react-pdf/issues/239#issuecomment-487255027
 */
export const ResumePDF = ({
  resume,
  settings,
  isPDF = false,
  selectedSection,
  enableHighlight = false,
  enableMistakeHighlight = false,
}: {
  resume: Resume;
  settings: Settings;
  isPDF?: boolean;
  selectedSection?: string;
  enableHighlight?: boolean;
  enableMistakeHighlight?: boolean;
}) => {
  const {
    profile,
    workExperiences,
    educations,
    projects,
    skills,
    certifications,
    languages,
    awards,
    custom,
  } = resume;
  const { name } = profile;
  const {
    fontFamily,
    fontSize,
    documentSize,
    formToHeading,
    formToShow,
    formsOrder,
    showBulletPoints,
  } = settings;
  const themeColor = settings.themeColor || DEFAULT_FONT_COLOR;

  const showFormsOrder = formsOrder.filter((form) => formToShow[form]);

  const formTypeToComponent: { [type in ShowForm]: () => JSX.Element } = {
    workExperiences: () => (
      <ResumePDFWorkExperience
        isSectionSelected={selectedSection === "experience"}
        heading={formToHeading["workExperiences"]}
        workExperiences={workExperiences}
        themeColor={themeColor}
        enableHighlight={enableHighlight}
        enableMistakeHighlight={enableMistakeHighlight}
      />
    ),
    educations: () => (
      <ResumePDFEducation
        isSectionSelected={selectedSection === "education"}
        heading={formToHeading["educations"]}
        educations={educations}
        themeColor={themeColor}
        showBulletPoints={showBulletPoints["educations"]}
        enableHighlight={enableHighlight}
        enableMistakeHighlight={enableMistakeHighlight}
      />
    ),
    projects: () => (
      <ResumePDFProject
        isSectionSelected={selectedSection === "projects"}
        heading={formToHeading["projects"]}
        projects={projects}
        themeColor={themeColor}
        isPDF={isPDF}
        enableHighlight={enableHighlight}
        enableMistakeHighlight={enableMistakeHighlight}
      />
    ),
    skills: () => (
      <ResumePDFSkills
        isSectionSelected={selectedSection === "skills"}
        heading={formToHeading["skills"]}
        skills={skills}
        themeColor={themeColor}
        showBulletPoints={showBulletPoints["skills"]}
        enableHighlight={enableHighlight}
      />
    ),
    certifications: () => (
      <ResumePDFCertificate
        isSectionSelected={selectedSection === "certifications"}
        heading={formToHeading["certifications"]}
        certifications={certifications}
        themeColor={themeColor}
        isPDF={isPDF}
      />
    ),
    languages: () => (
      <ResumePDFLanguage
        heading={formToHeading["languages"]}
        languages={languages}
        themeColor={themeColor}
        isPDF={isPDF}
      />
    ),
    awards: () => (
      <ResumePDFAward
        heading={formToHeading["awards"]}
        awards={awards}
        themeColor={themeColor}
        isPDF={isPDF}
      />
    ),
    custom: () => (
      <ResumePDFCustom
        heading={formToHeading["custom"]}
        custom={custom}
        themeColor={themeColor}
        showBulletPoints={showBulletPoints["custom"]}
        enableHighlight={enableHighlight}
        enableMistakeHighlight={enableMistakeHighlight}
      />
    ),
  };

  return (
    <>
      <Document title={`${name} Resume`} author={name} producer={"OpenResume"}>
        <Page
          size={documentSize === "A4" ? "A4" : "LETTER"}
          style={{
            ...styles.flexCol,
            color: DEFAULT_FONT_COLOR,
            fontFamily,
            fontSize: fontSize + "pt",
          }}
        >
          <FontSizeProvider fontSize={fontSize}>
            <View style={{ paddingTop: `${spacing[5]}` }} fixed={true}></View>
            <View
              style={{
                ...styles.flexCol,
                padding: `${spacing[0]} ${spacing[10]}`,
              }}
            >
              <ResumePDFProfile
                profile={profile}
                themeColor={themeColor}
                isPDF={isPDF}
                enableHighlight={enableHighlight}
                enableMistakeHighlight={enableMistakeHighlight}
              />
              {showFormsOrder.map((form) => {
                const Component = formTypeToComponent[form];
                return <Component key={form} />;
              })}
            </View>
            <View style={{ paddingBottom: `${spacing[5]}` }} fixed></View>
          </FontSizeProvider>
        </Page>
      </Document>
      <SuppressResumePDFErrorMessage />
    </>
  );
};
